import store from '@/store';
import { i18n } from '@/plugins/i18n';

// Constants for route paths to avoid tight coupling
const SETTINGS_PAYMENTS_PATH = '/settings/payments';
const DASHBOARD_PATH = '/dashboard';
const FEATURE_FLAGS_TIMEOUT = 5000;
const TOAST_TIMEOUT = 5000;

/**
 * Shows an error toast message
 * @param {string} messageKey - Translation key for the error message
 */
const showErrorToast = (messageKey) => {
  const translatedMessage = i18n.t(messageKey);

  store.dispatch('ToastStore/setToastConfig', {
    value: true,
    type: 'attention',
    bodyText: translatedMessage,
    timeout: TOAST_TIMEOUT,
  });
};

/**
 * Determines the appropriate redirect path based on the source route
 * @param {Object} obj - Object containing route properties
 * @param {string} obj.path - The route path 
 * @returns {string} - The redirect path as a string
 */
const getRedirectPath = ({path = DASHBOARD_PATH}) => {
  const isRootPath = path === '/';
  const isPaymentsSettingsPath = path.includes(SETTINGS_PAYMENTS_PATH);
  const shouldRedirectToDashboard = isRootPath || isPaymentsSettingsPath;
  
  return shouldRedirectToDashboard ? DASHBOARD_PATH : path;
};

const areFeatureFlagsLoaded = () => store.getters['BusinessStore/featureFlagsLoaded'];

const getPaymentPermissions = () => ({
  hasPaymentsModule: store.getters['BusinessStore/hasFeature']('payments_module'),
  hasPaymentsPromote: store.getters['BusinessStore/hasFeature']('pkg.payments.promote')
});

/**
 * Checks payment permissions and handles navigation
 * @param {Function} next - Navigation callback
 * @param {Object} from - Source route
 */
const handlePaymentPermissions = (next, from) => {
  const { hasPaymentsModule } = getPaymentPermissions();

  if (hasPaymentsModule) {
    return next();
  }
  
  showErrorToast('common.errors.insufficient_package');
  return next(getRedirectPath(from));
};

/**
 * Handles upgrade permissions (simpler logic)
 * @param {Function} next - Navigation callback
 * @param {Object} from - Source route
 */
const handleUpgradePermissions = (next, from) => {
  const { hasPaymentsModule, hasPaymentsPromote } = getPaymentPermissions();

  if (hasPaymentsModule || hasPaymentsPromote) {
    return next();
  }

  showErrorToast('common.errors.insufficient_package');
  return next(getRedirectPath(from));
};

const createFeatureFlagsPromise = () => {
  if (areFeatureFlagsLoaded()) {
    return Promise.resolve();
  }
  
  return new Promise((resolve) => {
    const unwatch = store.watch(
      (state, getters) => getters['BusinessStore/featureFlagsLoaded'],
      (loaded) => {
        if (loaded) {
          unwatch();
          resolve();
        }
      }
    );
  });
};

const createTimeoutPromise = () => new Promise((_, reject) => {
  setTimeout(reject, FEATURE_FLAGS_TIMEOUT);
});

const waitForFeatureFlags = async (next, from, permissionHandler) => {
  try {
    await Promise.race([
      createFeatureFlagsPromise(),
      createTimeoutPromise()
    ]);
    permissionHandler(next, from);
  } catch {
    showErrorToast('common.errors.insufficient_package');
    next(getRedirectPath(from));
  }
};

/**
 * Route guard for payment-related routes that require the payments_module feature flag
 */
export const paymentsModuleGuard = (to, from, next) => (areFeatureFlagsLoaded()
  ? handlePaymentPermissions(next, from)
  : waitForFeatureFlags(next, from, handlePaymentPermissions));

/**
 * Route guard for payment upgrade routes that allows access with either payments_module OR pkg.payments.promote
 * @param {Object} to - Target route
 * @param {Object} from - Source route
 * @param {Function} next - Navigation callback
 */
export const paymentsUpgradeGuard = (to, from, next) => (areFeatureFlagsLoaded()
  ? handleUpgradePermissions(next, from)
  : waitForFeatureFlags(next, from, handleUpgradePermissions));
